import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/validators/password';
import { AuthService } from '../../shared/services/auth.service';
import { StepperService } from '../stepper/stepper.service';
import { GoogleService, GoogleRegister } from '@sameta/shared';
import { BehaviorSubject, Subscription } from 'rxjs';
import { StrictEmailValidator } from '../../../../../../libs/shared/src/lib/validators/customEmailValidation';
import { GoogleAuthProvider, getAuth, signInWithPopup } from 'firebase/auth';

export class User {
  firstName: string;
  lastName: string;
  email: string;
  tenantId: string;
  password: string;
  company: string;
  marketing: boolean;
  privacyPolicy: boolean;
  language: string;
  firebaseId: string;
  isGoogle: boolean = false;
}
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: false,
})
export class RegisterComponent implements OnInit {
  registerForm: FormGroup;

  customValidators = {
    passwordFormControl: new FormControl('', {
      validators: Validators.compose([Validators.required, Validators.minLength(6), Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/)]),
    }),
    verifyPasswordFormControl: new FormControl('', { validators: Validators.compose([Validators.required]) }),
    emailFormControl: new FormControl('', [Validators.required, StrictEmailValidator.strictEmail()]),
  };

  hidePS1 = true;
  hidePS2 = true;
  cancel: string;
  inviteHash: string;
  invited = false;
  isGoogle: false;
  firebaseId: string;
  private googleSubscription: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly stepperService: StepperService,
    private readonly route: ActivatedRoute,
    private readonly googleData: GoogleService
  ) {
    this.registerForm = this.fb.group(
      {
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: this.customValidators.emailFormControl,
        tenantId: null,
        password: this.customValidators.passwordFormControl,
        verifyPassword: this.customValidators.verifyPasswordFormControl,
        company: new FormControl('', Validators.required),
        marketing: new FormControl(false),
        privacyPolicy: [false, Validators.required],
      },
      {
        validator: CustomValidators.mustMatch('password', 'verifyPassword'),
      }
    );
  }

  ngOnInit(): void {
    this.inviteHash = this.route.snapshot.queryParams.inviteHash as string;
    if (this.inviteHash) {
      this.stepperService.registeredCheck(this.inviteHash).subscribe((res) => {
        if (res.status === 200) {
          if (res.body.id !== 0) {
            this.registerForm.patchValue({
              email: res.body.email,
              company: res.body.tenant.name,
            });
            this.registerForm.controls.email.disable();
            this.registerForm.controls.company.disable();
            this.invited = true;
          }
        } else {
          this.router.navigate(['/error/expired'], {});
        }
      });
    }
    this.googleSubscription = this.googleData.currentGoogleUser$.subscribe((data) => {
      if (data) {
        this.isGoogle = data.isGoogle;
        this.firebaseId = data.uid;
        const parts: string[] = data.name.trim().split(/\s+/);
        const firstName: string = parts[0];
        const lastName: string = parts.slice(1).join(' ');
        this.registerForm.patchValue({
          firstName: firstName,
          lastName: lastName,
          email: data.email,
        });
        this.registerForm.controls.email.disable();
        if (this.isGoogle) {
          this.customValidators.passwordFormControl.clearValidators();
          this.customValidators.verifyPasswordFormControl.clearValidators();
        } else {
          this.customValidators.passwordFormControl.setValidators(
            Validators.compose([Validators.required, Validators.minLength(6), Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/)])
          );
          this.customValidators.verifyPasswordFormControl.setValidators(
            Validators.compose([Validators.required, Validators.minLength(6), Validators.pattern(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/)])
          );
        }
        this.customValidators.passwordFormControl.updateValueAndValidity();
        this.customValidators.verifyPasswordFormControl.updateValueAndValidity();
      }
    });
  }
  ngOnDestroy() {
    this.googleSubscription.unsubscribe();
  }

  toggleHidePassword(pass: string): void {
    if (pass === 'PS1') {
      this.hidePS1 = !this.hidePS1;
    }
    if (pass === 'PS2') {
      this.hidePS2 = !this.hidePS2;
    }
  }

  register(): void {
    // If form is invalid, mark all fields as touched to show validation errors
    if (this.registerForm.invalid) {
      Object.keys(this.registerForm.controls).forEach((key) => {
        const control = this.registerForm.get(key);
        control?.markAsTouched();
        control?.markAsDirty();
      });
      return;
    }

    this.translate.get('shared.cancel', { value: '' }).subscribe((resp: string) => {
      this.cancel = resp;
    });

    const user = new User();
    user.firstName = this.registerForm.get('firstName')?.value;
    user.lastName = this.registerForm.get('lastName')?.value;
    user.tenantId = this.registerForm.get('tenantId')?.value;
    user.password = this.registerForm.get('password')?.value;
    user.email = this.registerForm.get('email')?.value;
    user.company = this.registerForm.get('company')?.value;
    user.marketing = this.registerForm.get('marketing').value;
    user.privacyPolicy = this.registerForm.get('privacyPolicy').value;
    user.language = localStorage.getItem('language') !== null ? localStorage.getItem('language') : 'en';

    if (this.isGoogle) {
      if (this.registerForm.valid) {
        user.firebaseId = this.firebaseId;
        user.isGoogle = this.isGoogle;
        this.authService.register(user).subscribe({
          next: (res) => {
            if (res) {
              this.translate.get('register.success', { value: '' }).subscribe((resp: string) => {
                this.snackBar.open(resp, this.cancel, {
                  duration: 3000,
                });
                this.router.navigate(['/']);
              });
            }
          },
          error: (error) => {
            const errorMsg = typeof error === 'string' ? error : error.error?.message || error.error || error.message;
            if (error == 'ERROR_COMPANY' || errorMsg == 'ERROR_COMPANY') {
              this.translate.get('login.alreadyRegisteredCompany', { value: '' }).subscribe((resp: string) => {
                this.snackBar.open(resp, this.cancel, {
                  duration: 3000,
                });
              });
            } else if (error == 'EMAIL_EXIST' || errorMsg == 'EMAIL_EXIST') {
              this.translate.get('auth.register.emailExists', { value: '' }).subscribe((resp: string) => {
                this.snackBar.open(resp, this.cancel, {
                  duration: 3000,
                });
              });
            } else {
              this.translate.get('register.notRegister', { value: '' }).subscribe((resp: string) => {
                this.snackBar.open(resp, this.cancel, {
                  duration: 3000,
                });
              });
            }
          },
        });
      }
    }
    if (this.invited && !this.isGoogle) {
      this.authService.registerInvitedUser(user, this.inviteHash).subscribe(
        (res) => {
          if (res) {
            let uploadMsg;
            this.translate.get('register.success', { value: '' }).subscribe((resp: string) => {
              uploadMsg = resp;
            });
            this.snackBar.open(uploadMsg, this.cancel, {
              duration: 3000,
            });
            this.router.navigate(['/'], {});
          }
        },
        (error) => {
          let uploadMsg;
          this.translate.get('register.notRegister', { value: '' }).subscribe((resp: string) => {
            uploadMsg = resp;
          });
          this.snackBar.open(uploadMsg, this.cancel, {
            duration: 3000,
          });
        }
      );
    }

    if (!this.invited && !this.isGoogle) {
      if (this.registerForm.valid) {
        this.authService.register(user).subscribe(
          (res) => {
            if (res) {
              let uploadMsg;
              this.translate.get('register.success', { value: '' }).subscribe((resp: string) => {
                uploadMsg = resp;
              });
              this.snackBar.open(uploadMsg, this.cancel, {
                duration: 3000,
              });
              this.router.navigate(['/'], {});
            }
          },
          (error) => {
            let uploadMsg;
            const errorMsg = typeof error === 'string' ? error : error.error?.message || error.error || error.message;

            if (error == 'ERROR_COMPANY' || errorMsg == 'ERROR_COMPANY') {
              this.translate.get('login.alreadyRegisteredCompany', { value: '' }).subscribe((resp: string) => {
                uploadMsg = resp;
              });
            } else if (errorMsg == 'EMAIL_EXIST') {
              this.translate.get('login.alreadyExistEmail', { value: '' }).subscribe((resp: string) => {
                uploadMsg = resp;
              });
            } else {
              this.translate.get('register.notRegister', { value: '' }).subscribe((resp: string) => {
                uploadMsg = resp;
              });
            }
            this.snackBar.open(uploadMsg, this.cancel, {
              duration: 3000,
            });
          }
        );
      }
    }
  }

  googleAuth = async (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    const auth = getAuth();
    signInWithPopup(auth, provider)
      .then((result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const user = result.user;
        let newUser = new GoogleRegister();
        newUser.isGoogle = true;
        newUser.name = user.displayName;
        newUser.email = user.email;
        newUser.emailVerified = user.emailVerified;
        newUser.uid = user.uid;
        if (user.emailVerified == true) {
          user.getIdToken().then((idToken) => {
            this.googleData.sendGoogleUserData(newUser);

            this.authService.googleRegister(newUser).subscribe({
              next: (res) => {
                if (res === 'NOT_REGISTERED') {
                  // User not registered yet - stay on register page to complete registration
                  // The form will be pre-filled by googleSubscription in ngOnInit
                } else if (res === 'REGISTERED') {
                  // User already registered - log them in and redirect to dashboard
                  this.authService.login(idToken).subscribe({
                    next: (res) => {
                      // Apply user's language from backend
                      const userLanguage = res.language || 'en';
                      this.translate.use(userLanguage);
                      this.router.navigate(['/dashboard']);
                    },
                    error: (error) => {
                      let cancel;
                      let uploadMsg;

                      this.translate.get('shared.cancel', { value: '' }).subscribe((resp: string) => {
                        cancel = resp;
                      });
                      const errorMsg = typeof error === 'string' ? error : error.error?.message || error.error || error.message;
                      if (error == 'USER_NOT_ACTIVE' || errorMsg == 'USER_NOT_ACTIVE') {
                        this.translate.get('login.twFirstConfirmation', { value: '' }).subscribe((resp: string) => {
                          uploadMsg = resp;
                        });
                      } else if (error == 'EMAIL_NOT_VERIFIED' || errorMsg == 'EMAIL_NOT_VERIFIED') {
                        this.translate.get('login.emailNotVerified', { value: '' }).subscribe((resp: string) => {
                          uploadMsg = resp;
                        });
                      } else {
                        this.translate.get('login.unauthorized', { value: '' }).subscribe((resp: string) => {
                          uploadMsg = resp;
                        });
                      }
                      this.snackBar.open(`${uploadMsg}`, cancel, {
                        duration: 4000,
                        panelClass: ['snackbar-formater'],
                      });
                    },
                  });
                }
              },
              error: (error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
              },
            });
          });
        }
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
      });
  };

  protected readonly length = length;
}
